// PreviewAbout.jsx
import { CheckCircleOutlined } from "@ant-design/icons"
import { Card, Col, Row, Tag } from "antd"
import PhotoSlider from "components/common/PhotoSlider"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import IconCard from "../IconCard"

// All preview list items share one icon — keeps the section visually uniform.
const COMMON_ICON = <CheckCircleOutlined />

// snake_case / lowercase keys → sentence case ("fishing_lure" → "Fishing lure").
// Already-formatted strings ("Wheelchair accessible") pass through unchanged.
const humanize = (value) =>
  String(value ?? "")
    .replaceAll("_", " ")
    .replace(/^\w/, (char) => char.toUpperCase())

// Build an { id → name } lookup from a list of { id, name } options.
const buildLabelMap = (options) =>
  Object.fromEntries((options || []).map((option) => [option.id, option.name]))

export const PreviewAbout = ({ trip, t, embedded, hideHeader }) => {
  const { description, alcohol_allowed, accessibility_enabled, bring_items, included_items, skill_levels, children_allowed, boats } = trip

  // included_items / bring_items are stored as option IDs; resolve their names
  // from the experience options. Falls back to humanize for already-named values.
  const experienceOptions = useSelector((state) => state.trips.experienceOptions?.data)
  const includedLabelMap = buildLabelMap(experienceOptions?.included_items)
  const bringLabelMap = buildLabelMap(experienceOptions?.bring_items)
  const resolveLabel = (labelMap) => (item) =>
    labelMap[item] ?? (typeof item === "object" ? item?.name : null) ?? humanize(item)

  const headingClass = embedded ? "h5" : "h3"
  const sectionClass = embedded ? "mt-20" : "mt-40"
  const colSpan = embedded ? 8 : 12

  // Generic card grid — every section renders the same way with the common icon.
  // Dedupe so a repeated id (the API can return the same item twice) renders once.
  const renderCardSection = (title, items, getLabel = humanize) => (
    <div className={sectionClass}>
      <h2 className={headingClass}>{title}</h2>
      <Row gutter={[16, 16]}>
        {[...new Set(items)].map((item) => (
          <Col xs={24} sm={colSpan} key={item}>
            <IconCard icon={COMMON_ICON} label={getLabel(item)} className={embedded ? "embedded" : ""} />
          </Col>
        ))}
      </Row>
    </div>
  )

  const otherItems = []
  if (accessibility_enabled) otherItems.push("Wheelchair accessible")
  if (children_allowed) otherItems.push("Child friendly")
  if (alcohol_allowed) otherItems.push("Alcohol allowed")

  return (
    <>
      {/* About Section — can be hidden when a parent renders its own About header. */}
      {!hideHeader && (
        <div className={sectionClass}>
          <h2 className={headingClass} style={{ color: "#272A34", paddingBottom: embedded ? "6px" : "16px" }}>
            {t("global.about")}
          </h2>
          <p className="text-prewrap">{description}</p>
        </div>
      )}

      {/* What's Included Section */}
      {included_items?.length > 0 && renderCardSection(t("trip.labels.included_items"), included_items, resolveLabel(includedLabelMap))}

      {/* What to Bring Section */}
      {bring_items?.length > 0 && renderCardSection(t("trip.labels.bring_items"), bring_items, resolveLabel(bringLabelMap))}

      {/* Not Allowed to Bring Section */}
      {!alcohol_allowed && renderCardSection(t("trip.labels.not_allowed_to_bring_items"), ["alcohol"], () => "Alcohol is not allowed")}

      {/* Recommended for Skill Levels Section */}
      {skill_levels?.length > 0 && renderCardSection(t("trip.labels.recommended_skills"), skill_levels)}

      {/* Other Section */}
      {otherItems.length > 0 && renderCardSection(t("trip.labels.other"), otherItems)}

      {/* Equipment Section */}
      {boats?.length > 0 && (
        <div className={embedded ? "mt-20" : "mt-45"}>
          <h2 className={headingClass}>{t("trip.labels.equipment")}</h2>
          {boats.map((item) => {
            const characteristics = item.characteristics || []
            return (
              <Card key={item.id} style={{ borderRadius: "8px", marginBottom: "20px" }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={6}>
                    <PhotoSlider photos={item.properties} cropped_photos={item.cropped_photos} preview />
                  </Col>
                  <Col xs={24} sm={16} style={{ marginLeft: "20px" }}>
                    <h3 className={headingClass}>{item.name || item.equipment_title}</h3>
                    <p style={{ display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {item.description}
                    </p>
                    <div>
                      {characteristics.map((char) => (
                        <Tag key={char.id} style={{ marginBottom: "8px" }}>
                          {char.name}
                        </Tag>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Card>
            )
          })}
        </div>
      )}
    </>
  )
}

PreviewAbout.propTypes = {
  trip: PropTypes.shape({
    description: PropTypes.string,
    alcohol_allowed: PropTypes.bool,
    accessibility_enabled: PropTypes.bool,
    bring_items: PropTypes.arrayOf(PropTypes.string),
    included_items: PropTypes.arrayOf(PropTypes.string).isRequired,
    skill_levels: PropTypes.arrayOf(PropTypes.string),
    children_allowed: PropTypes.bool,
    media: PropTypes.array,
    boats: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
        description: PropTypes.string,
        properties: PropTypes.array, // Assuming this is image/media data for PhotoSlider
        custom_info: PropTypes.arrayOf(PropTypes.string) // Tags
      })
    )
  }).isRequired,
  t: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
  hideHeader: PropTypes.bool
}

export default PreviewAbout
